'use client';

import React, { useId } from 'react';
import { Colors } from '@/lib/colorUtils';
import { cn } from '@/lib/styleUtils';
import {
  circleLoaderWrapperVariants,
  CircleLoaderProps,
  CircleLoaderSize,
} from './CircleLoader.style';

/** Ring path from reference Loader.svg: outer r=10, inner r=6.5, center (12,12) */
const RING_PATH =
  'M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM5.5 12C5.5 15.5898 8.41015 18.5 12 18.5C15.5898 18.5 18.5 15.5898 18.5 12C18.5 8.41015 15.5898 5.5 12 5.5C8.41015 5.5 5.5 8.41015 5.5 12Z';

/** Resolve color to CSS value for conic-gradient */
const colorMap: Record<Colors, string> = {
  [Colors.colorPrimary]: 'var(--tw-color-primary)',
  [Colors.colorPrimaryLight]: 'var(--tw-color-primary-light)',
  [Colors.colorPrimaryDark]: 'var(--tw-color-primary-dark)',
  [Colors.colorPrimaryDarker]: 'var(--tw-color-primary-darker)',
  [Colors.colorBrandError]: 'var(--tw-color-error)',
  [Colors.colorBrandWarn]: 'var(--tw-color-warn)',
  [Colors.colorBrandWhite]: 'var(--tw-color-white)',
  [Colors.colorGray]: 'var(--tw-color-gray)',
  [Colors.colorGrayLight]: 'var(--tw-color-gray-light)',
  [Colors.colorGrayLighter]: 'var(--tw-color-gray-lighter)',
  [Colors.colorGrayDark]: 'var(--tw-color-gray-dark)',
  [Colors.colorGrayDarker]: 'var(--tw-color-gray-darker)',
  [Colors.colorFont]: 'var(--tw-color-font)',
  [Colors.colorLogoBackground]: 'var(--tw-color-logo-background)',
  [Colors.colorWhiteLabelBackground]: 'var(--tw-color-whitelabel-background)',
  [Colors.colorBlack]: 'var(--tw-color-black)',
  [Colors.colorWhiteLabelSiteHeader]: 'var(--tw-color-whitelabel-header)',
  [Colors.colorAccent]: 'var(--tw-color-accent)',
  [Colors.colorAccentDark]: 'var(--tw-color-accent-dark)',
};

/**
 * Circle loader matching reference Loader.svg: donut ring with conic gradient
 * (from 90deg: transparent → blue → transparent). Uses clip-path + conic-gradient.
 */
export const CircleLoader = ({
  size = CircleLoaderSize.medium,
  color = Colors.colorPrimary,
  className = '',
  'data-cy': dataCy = 'circle-loader',
}: CircleLoaderProps) => {
  const id = useId();
  const clipId = `circle-loader-clip-${id.replace(/:/g, '')}`;
  const primaryColor = colorMap[color];
  const conicGradient = `conic-gradient(from 90deg, transparent 0deg, ${primaryColor} 359.964deg, transparent 360deg)`;

  return (
    <span
      className={cn(circleLoaderWrapperVariants({ size }), className)}
      aria-hidden
      role="img"
      aria-label="Loading"
      data-cy={dataCy}
    >
      <svg
        className="tw:h-full tw:w-full tw:block"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <clipPath id={clipId}>
            <path d={RING_PATH} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <foreignObject x="0" y="0" width="24" height="24">
            <div
              className="tw:w-6 tw:h-6"
              // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed conic-gradient ring)
              style={{
                background: conicGradient,
              }}
            />
          </foreignObject>
        </g>
      </svg>
    </span>
  );
};

export { CircleLoaderSize };
export type { CircleLoaderProps };
